import React, { useEffect, useId, useState } from "react";

/**
 * Bottom-bowl Peak readiness gauge. Flat top, curved bottom, progress along
 * the rim from top-left through the bottom to top-right. Fill and stroke fade
 * from transparent at the top to the indicator colour at the bottom — the
 * same bowl used on the app dashboard.
 */

const VB_W = 340;
const VB_H = 200;
const INSET = 8;

const BOWL = (() => {
  const left = INSET;
  const top = INSET;
  const right = VB_W - INSET;
  const cx = (left + right) / 2;
  const cy = top;
  const rx = (right - left) / 2;
  const ry = VB_H - INSET - top;

  let rim = "";
  const rimSteps = 64;
  for (let i = 0; i <= rimSteps; i++) {
    const p = i / rimSteps;
    const a = Math.PI + p * Math.PI;
    const x = cx + rx * Math.cos(a);
    const y = cy - ry * Math.sin(a);
    rim += `${i === 0 ? "M" : "L"}${x.toFixed(2)} ${y.toFixed(2)}`;
  }

  let fill = `M${left} ${top}L${right} ${top}`;
  const fillSteps = 64;
  for (let i = 1; i <= fillSteps; i++) {
    const a = (i / fillSteps) * Math.PI;
    const x = cx + rx * Math.cos(a);
    const y = cy + ry * Math.sin(a);
    fill += `L${x.toFixed(2)} ${y.toFixed(2)}`;
  }
  fill += "Z";

  return { rim, fill };
})();

interface PeakHalfOvalProps {
  /** Fraction filled along the bowl rim, 0...1. */
  progress: number;
  color: string;
  /**
   * On mount, draw the faint rim first, then ease the score arc in.
   * Matches the readiness intro on the dashboard.
   */
  animateIn?: boolean;
  /** Stroke transition when `progress` changes. Ignored during the intro draw. */
  durationMs?: number;
  className?: string;
  children?: React.ReactNode;
}

const PeakHalfOval: React.FC<PeakHalfOvalProps> = ({
  progress,
  color,
  animateIn = false,
  durationMs = 450,
  className = "",
  children,
}) => {
  const rawId = useId().replace(/:/g, "");
  const fillId = `bowl-fill-${rawId}`;
  const trackId = `bowl-track-${rawId}`;
  const progressId = `bowl-progress-${rawId}`;

  const clamped = Math.min(Math.max(progress, 0), 1);
  const [outline, setOutline] = useState(animateIn ? 0 : 1);
  const [revealed, setRevealed] = useState(!animateIn);

  useEffect(() => {
    if (!animateIn) {
      setOutline(1);
      setRevealed(true);
      return;
    }
    const reduce =
      typeof window !== "undefined" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduce) {
      setOutline(1);
      setRevealed(true);
      return;
    }
    setOutline(0);
    setRevealed(false);
    const frame = requestAnimationFrame(() => setOutline(1));
    const timer = window.setTimeout(() => setRevealed(true), 380);
    return () => {
      cancelAnimationFrame(frame);
      window.clearTimeout(timer);
    };
  }, [animateIn]);

  const shown = revealed ? clamped : 0;

  return (
    <div
      className={`relative mx-auto w-full max-w-[340px] ${className}`}
      style={{ aspectRatio: `${VB_W} / ${VB_H}` }}
    >
      <svg
        viewBox={`0 0 ${VB_W} ${VB_H}`}
        className="h-full w-full overflow-visible"
        aria-hidden="true"
      >
        <defs>
          <linearGradient id={fillId} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0" stopColor={color} stopOpacity="0" />
            <stop offset="0.08" stopColor={color} stopOpacity="0" />
            <stop offset="0.38" stopColor={color} stopOpacity={0.4 * 0.45} />
            <stop offset="1" stopColor={color} stopOpacity="0.4" />
          </linearGradient>
          <linearGradient id={trackId} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0" stopColor={color} stopOpacity="0" />
            <stop offset="0.08" stopColor={color} stopOpacity="0" />
            <stop offset="0.38" stopColor={color} stopOpacity={0.35 * 0.5} />
            <stop offset="1" stopColor={color} stopOpacity="0.35" />
          </linearGradient>
          <linearGradient id={progressId} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0" stopColor={color} stopOpacity="0" />
            <stop offset="0.08" stopColor={color} stopOpacity="0" />
            <stop offset="0.38" stopColor={color} stopOpacity="0.5" />
            <stop offset="1" stopColor={color} stopOpacity="1" />
          </linearGradient>
        </defs>
        <path d={BOWL.fill} fill={`url(#${fillId})`} />
        <path
          d={BOWL.rim}
          fill="none"
          stroke={`url(#${trackId})`}
          strokeWidth={9}
          strokeLinecap="round"
          strokeLinejoin="round"
          pathLength={1}
          strokeDasharray="1 1"
          strokeDashoffset={1 - outline}
          style={{
            transition: animateIn ? "stroke-dashoffset 1100ms ease-in-out" : undefined,
          }}
        />
        <path
          d={BOWL.rim}
          fill="none"
          stroke={`url(#${progressId})`}
          strokeWidth={9}
          strokeLinecap="round"
          strokeLinejoin="round"
          pathLength={1}
          strokeDasharray="1 1"
          strokeDashoffset={1 - shown}
          style={{ transition: `stroke-dashoffset ${durationMs}ms ease-out` }}
        />
      </svg>
      {children && (
        <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
          <div
            className="flex flex-col items-center"
            style={{
              opacity: revealed ? 1 : 0,
              transition: animateIn ? "opacity 600ms ease-in" : undefined,
            }}
          >
            {children}
          </div>
        </div>
      )}
    </div>
  );
};

export default PeakHalfOval;
